import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Trophy, ToggleLeft, ToggleRight } from 'lucide-react'
import { api } from '../lib/api'

const GAME_TYPE_LABELS: Record<string, string> = {
  spin_wheel:  'Spin the Wheel',
  scratch_win: 'Scratch & Win',
  lottery:     'Lottery Draw',
  mystery_box: 'Mystery Box',
  slot_machine: 'Slot Machine',
}

const PRIZE_TYPE_LABELS: Record<string, string> = {
  drops:         '💧 Drops',
  loyalty_points:'⭐ Loyalty Points',
  discount_code: '🏷️ Discount Code',
  free_delivery: '🚚 Free Delivery',
  physical_item: '🎁 Physical Item',
  none:          '❌ No Prize (Lose)',
}

const FREQ_LABELS: Record<string, string> = {
  daily:     'Once daily',
  per_order: 'Once per order',
  unlimited: 'Unlimited',
  one_time:  'One time only',
}

interface Prize {
  id: string; game_id: string; name_en: string; name_ar: string
  prize_type: string; value: number; discount_type: string | null
  description: string | null; weight: number
  quantity_limit: number | null; remaining: number | null
  color: string | null; icon: string | null; sort_order: number
}

interface Game {
  id: string; type: string; name_en: string; name_ar: string
  description_en: string | null; active: number
  play_cost_drops: number; play_frequency: string
  starts_at: string | null; ends_at: string | null
  sort_order: number; total_plays: number
}

interface GameDetail extends Game { description_ar: string | null; config: string }

// ── Prize form ────────────────────────────────────────────────────────────────

interface ProductHit {
  id: string; sku: string; name: string; name_ar: string
  brand: string | null; price_fils: number; size_ml: number | null
}

function ProductSearch({ onSelect }: { onSelect: (p: ProductHit) => void }) {
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 300)
    return () => clearTimeout(t)
  }, [term])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const { data } = useQuery<{ products: ProductHit[] }>({
    queryKey: ['product-search', debounced],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(debounced)}&limit=8`).then(r => r.data),
    enabled: debounced.length >= 2,
  })

  const inp = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]'

  return (
    <div ref={ref} className="relative">
      <input
        className={inp}
        placeholder="Search products by name, brand, or SKU…"
        value={term}
        onChange={e => { setTerm(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
      />
      {open && debounced.length >= 2 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-xl max-h-64 overflow-y-auto">
          {!data?.products?.length ? (
            <p className="text-gray-500 text-xs px-4 py-3">No products found</p>
          ) : (
            data.products.map(p => (
              <button
                key={p.id}
                type="button"
                className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/5 last:border-0 transition-colors"
                onClick={() => { onSelect(p); setTerm(''); setOpen(false) }}
              >
                <p className="text-white text-sm font-medium leading-tight">{p.name}</p>
                <p className="text-gray-400 text-xs mt-0.5">
                  {[p.brand, p.size_ml ? `${p.size_ml}ml` : null, `JD ${(p.price_fils / 1000).toFixed(3)}`]
                    .filter(Boolean).join(' · ')}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function PrizeForm({ gameId, prize, onDone }: {
  gameId: string
  prize?: Prize
  onDone: () => void
}) {
  const qc = useQueryClient()
  const [nameEn, setNameEn] = useState(prize?.name_en ?? '')
  const [nameAr, setNameAr] = useState(prize?.name_ar ?? '')
  const [prizeType, setPrizeType] = useState(prize?.prize_type ?? 'drops')
  const [value, setValue] = useState(String(prize?.value ?? 0))
  const [discountType, setDiscountType] = useState(prize?.discount_type ?? 'percentage')
  const [description, setDescription] = useState(prize?.description ?? '')
  const [weight, setWeight] = useState(String(prize?.weight ?? 1))
  const [quantityLimit, setQuantityLimit] = useState(prize?.quantity_limit != null ? String(prize.quantity_limit) : '')
  const [color, setColor] = useState(prize?.color ?? '#c41e3a')
  const [icon, setIcon] = useState(prize?.icon ?? '')
  const [sortOrder, setSortOrder] = useState(String(prize?.sort_order ?? 0))
  const [error, setError] = useState('')

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name_en: prizeType === 'none' ? 'No Prize' : nameEn.trim(),
        name_ar: prizeType === 'none' ? 'لا جائزة' : (nameAr.trim() || nameEn.trim()),
        prize_type: prizeType, value: prizeType === 'none' ? 0 : parseInt(value || '0'),
        discount_type: prizeType === 'discount_code' ? discountType : undefined,
        description: description.trim() || undefined,
        weight: parseInt(weight || '1'),
        quantity_limit: quantityLimit ? parseInt(quantityLimit) : undefined,
        color: color || undefined, icon: icon.trim() || undefined,
        sort_order: parseInt(sortOrder || '0'),
      }
      return prize
        ? api.patch(`/games/${gameId}/prizes/${prize.id}`, payload).then(r => r.data)
        : api.post(`/games/${gameId}/prizes`, payload).then(r => r.data)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['game-detail', gameId] }); onDone() },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  })

  const [selectedProduct, setSelectedProduct] = useState<ProductHit | null>(null)

  const handleProductSelect = (p: ProductHit) => {
    setSelectedProduct(p)
    setNameEn(p.name)
    setNameAr(p.name_ar || p.name)
    setValue(String(p.price_fils))
    setDescription(`SKU: ${p.sku}${p.brand ? ` · ${p.brand}` : ''}${p.size_ml ? ` · ${p.size_ml}ml` : ''}`)
  }

  const inp = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]'
  const label = 'block text-xs text-gray-400 mb-1'

  return (
    <div className="bg-[#222] rounded-xl border border-white/10 p-4 space-y-3">
      <p className="text-sm font-semibold text-white">{prize ? 'Edit Prize' : 'Add Prize'}</p>

      <div>
        <label className={label}>Prize Type</label>
        <select className={inp} value={prizeType} onChange={e => { setPrizeType(e.target.value); setSelectedProduct(null) }}>
          {Object.entries(PRIZE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k} className="bg-[#1a1a1a]">{v}</option>)}
        </select>
      </div>

      {prizeType === 'none' ? (
        <p className="text-xs text-gray-400 bg-white/5 rounded-lg px-3 py-2">
          This slot results in no prize. Use the weight below to control how often players lose.
        </p>
      ) : prizeType === 'physical_item' ? (
        <>
          <div>
            <label className={label}>Search product</label>
            {selectedProduct ? (
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                <div>
                  <p className="text-white text-sm font-medium">{selectedProduct.name}</p>
                  <p className="text-gray-400 text-xs">
                    {[selectedProduct.brand, selectedProduct.size_ml ? `${selectedProduct.size_ml}ml` : null, `JD ${(selectedProduct.price_fils / 1000).toFixed(3)}`]
                      .filter(Boolean).join(' · ')}
                  </p>
                </div>
                <button type="button" onClick={() => setSelectedProduct(null)} className="text-gray-500 hover:text-white text-xs ml-3 shrink-0">✕ Clear</button>
              </div>
            ) : (
              <ProductSearch onSelect={handleProductSelect} />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Name (EN)</label><input className={inp} value={nameEn} onChange={e => setNameEn(e.target.value)} /></div>
            <div><label className={label}>Name (AR)</label><input className={inp} value={nameAr} onChange={e => setNameAr(e.target.value)} dir="rtl" /></div>
          </div>
          <div>
            <label className={label}>Retail price (fils, auto-filled)</label>
            <input className={inp} type="number" min="0" value={value} onChange={e => setValue(e.target.value)} />
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Name (EN)</label><input className={inp} value={nameEn} onChange={e => setNameEn(e.target.value)} placeholder="e.g. 50 Drops" /></div>
            <div><label className={label}>Name (AR)</label><input className={inp} value={nameAr} onChange={e => setNameAr(e.target.value)} placeholder="e.g. ٥٠ نقطة" /></div>
          </div>
          <div>
            <label className={label}>
              {prizeType === 'drops' ? 'Drops amount' : prizeType === 'loyalty_points' ? 'Points amount' : prizeType === 'discount_code' ? 'Discount value' : 'Value'}
            </label>
            <input className={inp} type="number" min="0" value={value} onChange={e => setValue(e.target.value)} />
          </div>
        </>
      )}

      {prizeType === 'discount_code' && (
        <div>
          <label className={label}>Discount type</label>
          <select className={inp} value={discountType} onChange={e => setDiscountType(e.target.value)}>
            <option value="percentage" className="bg-[#1a1a1a]">Percentage (%)</option>
            <option value="fixed" className="bg-[#1a1a1a]">Fixed amount (fils)</option>
          </select>
        </div>
      )}
      {prizeType === 'physical_item' && (
        <div><label className={label}>Admin notes</label><input className={inp} value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. DrinX branded tote bag" /></div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div><label className={label}>Weight (probability)</label><input className={inp} type="number" min="1" value={weight} onChange={e => setWeight(e.target.value)} /></div>
        <div><label className={label}>Qty limit (blank = ∞)</label><input className={inp} type="number" min="1" value={quantityLimit} onChange={e => setQuantityLimit(e.target.value)} placeholder="∞" /></div>
        <div><label className={label}>Sort order</label><input className={inp} type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Segment color</label>
          <div className="flex gap-2 items-center">
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="h-9 w-12 rounded cursor-pointer bg-transparent border border-white/10" />
            <input className={inp} value={color} onChange={e => setColor(e.target.value)} placeholder="#c41e3a" />
          </div>
        </div>
        <div><label className={label}>Icon (emoji)</label><input className={inp} value={icon} onChange={e => setIcon(e.target.value)} placeholder="🎁" /></div>
      </div>
      {error && <p className="text-red-400 text-xs">{error}</p>}
      <div className="flex gap-2 pt-1">
        <button onClick={() => save.mutate()} disabled={save.isPending} className="px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50">
          {save.isPending ? 'Saving…' : 'Save Prize'}
        </button>
        <button onClick={onDone} className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 text-sm hover:bg-white/10">Cancel</button>
      </div>
    </div>
  )
}

// ── Game detail panel ─────────────────────────────────────────────────────────

function GamePanel({ gameId, onClose }: { gameId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [addingPrize, setAddingPrize] = useState(false)
  const [editingPrize, setEditingPrize] = useState<Prize | null>(null)

  const { data, isLoading } = useQuery<{ game: GameDetail; prizes: Prize[] }>({
    queryKey: ['game-detail', gameId],
    queryFn: () => api.get(`/games/${gameId}`).then(r => r.data),
  })

  const deletePrize = useMutation({
    mutationFn: (prizeId: string) => api.delete(`/games/${gameId}/prizes/${prizeId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['game-detail', gameId] }),
  })

  if (isLoading) return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#111] border-l border-[#2e2e2e] h-full flex items-center justify-center">
        <p className="text-gray-500 text-sm">Loading…</p>
      </div>
    </div>
  )

  const game = data!.game
  const prizes = data!.prizes ?? []
  const totalWeight = prizes.reduce((s, p) => s + p.weight, 0)

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#111] border-l border-[#2e2e2e] h-full overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e] sticky top-0 bg-[#111] z-10">
          <div>
            <p className="text-white font-semibold">{game.name_en}</p>
            <p className="text-gray-500 text-xs mt-0.5">{GAME_TYPE_LABELS[game.type]} · {FREQ_LABELS[game.play_frequency]}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1">✕</button>
        </div>

        <div className="p-5 space-y-6 flex-1">
          {/* Prizes */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Prize Table</p>
              <button onClick={() => { setAddingPrize(true); setEditingPrize(null) }}
                className="flex items-center gap-1 text-xs text-[#c41e3a] hover:text-[#e02040]">
                <Plus size={12} /> Add prize
              </button>
            </div>

            {addingPrize && <div className="mb-3"><PrizeForm gameId={gameId} onDone={() => setAddingPrize(false)} /></div>}

            {prizes.length === 0 && !addingPrize && (
              <p className="text-gray-600 text-sm">No prizes yet — add one above.</p>
            )}

            <div className="space-y-2">
              {prizes.map(p => {
                const pct = totalWeight > 0 ? Math.round((p.weight / totalWeight) * 100) : 0
                return editingPrize?.id === p.id ? (
                  <PrizeForm key={p.id} gameId={gameId} prize={p} onDone={() => setEditingPrize(null)} />
                ) : (
                  <div key={p.id} className="flex items-center gap-3 bg-[#1a1a1a] rounded-lg px-3 py-2.5">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: p.color ?? '#888' }} />
                    <span className="text-lg shrink-0">{p.icon ?? '🎁'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{p.name_en}</p>
                      <p className="text-gray-500 text-xs">{PRIZE_TYPE_LABELS[p.prize_type]}{p.value ? ` · ${p.value}` : ''}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-gray-400 text-xs">{pct}%</p>
                      {p.remaining !== null && <p className="text-gray-600 text-xs">{p.remaining} left</p>}
                    </div>
                    <button onClick={() => { setEditingPrize(p); setAddingPrize(false) }} className="text-gray-600 hover:text-white p-1"><Pencil size={12} /></button>
                    <button onClick={() => { if (confirm('Delete this prize?')) deletePrize.mutate(p.id) }} className="text-gray-600 hover:text-red-400 p-1"><Trash2 size={12} /></button>
                  </div>
                )
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

// ── Game form modal ───────────────────────────────────────────────────────────

function GameForm({ game, onDone }: { game?: Game; onDone: () => void }) {
  const qc = useQueryClient()
  const [nameEn, setNameEn] = useState(game?.name_en ?? '')
  const [nameAr, setNameAr] = useState(game?.name_ar ?? '')
  const [type, setType] = useState(game?.type ?? 'spin_wheel')
  const [frequency, setFrequency] = useState(game?.play_frequency ?? 'daily')
  const [costDrops, setCostDrops] = useState(String(game?.play_cost_drops ?? 0))
  const [startsAt, setStartsAt] = useState(game?.starts_at?.slice(0, 16) ?? '')
  const [endsAt, setEndsAt] = useState(game?.ends_at?.slice(0, 16) ?? '')
  const [sortOrder, setSortOrder] = useState(String(game?.sort_order ?? 0))
  const [error, setError] = useState('')

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name_en: nameEn.trim(), name_ar: nameAr.trim() || nameEn.trim(),
        type, play_frequency: frequency,
        play_cost_drops: parseInt(costDrops || '0'),
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        sort_order: parseInt(sortOrder || '0'),
      }
      return game
        ? api.patch(`/games/${game.id}`, payload).then(r => r.data)
        : api.post('/games', payload).then(r => r.data)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['games'] }); onDone() },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save'),
  })

  const inp = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]'
  const lbl = 'block text-xs text-gray-400 mb-1'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onDone} />
      <div className="relative bg-[#1a1a1a] rounded-2xl border border-white/10 p-6 w-full max-w-2xl space-y-4">
        <p className="text-white font-semibold text-lg">{game ? 'Edit Game' : 'New Game'}</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Name (EN)</label><input className={inp} value={nameEn} onChange={e => setNameEn(e.target.value)} placeholder="e.g. Daily Spin" /></div>
          <div><label className={lbl}>Name (AR)</label><input className={inp} value={nameAr} onChange={e => setNameAr(e.target.value)} placeholder="e.g. الدوّارة اليومية" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Game type</label>
            <select className={inp} value={type} onChange={e => setType(e.target.value)} disabled={!!game}>
              {Object.entries(GAME_TYPE_LABELS).map(([k, v]) => <option key={k} value={k} className="bg-[#1a1a1a]">{v}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Play frequency</label>
            <select className={inp} value={frequency} onChange={e => setFrequency(e.target.value)}>
              {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k} className="bg-[#1a1a1a]">{v}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={lbl}>Cost (Drops, 0 = free)</label><input className={inp} type="number" min="0" value={costDrops} onChange={e => setCostDrops(e.target.value)} /></div>
          <div><label className={lbl}>Sort order</label><input className={inp} type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Starts at (optional)</label><input className={inp} type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)} /></div>
          <div><label className={lbl}>Ends at (optional)</label><input className={inp} type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)} /></div>
        </div>
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button onClick={() => save.mutate()} disabled={save.isPending} className="px-5 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onDone} className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 text-sm hover:bg-white/10">Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ── Main Games page ───────────────────────────────────────────────────────────

export default function Games() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingGame, setEditingGame] = useState<Game | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ games: Game[] }>({
    queryKey: ['games'],
    queryFn: () => api.get('/games').then(r => r.data),
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: number }) =>
      api.patch(`/games/${id}`, { active: active === 1 ? 0 : 1 }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['games'] }),
  })

  const deleteGame = useMutation({
    mutationFn: (id: string) => api.delete(`/games/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['games'] }),
  })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Trophy size={20} className="text-[#c41e3a]" />
          <h1 className="text-lg font-semibold text-white">Games</h1>
        </div>
        <button onClick={() => { setShowForm(true); setEditingGame(null) }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]">
          <Plus size={14} /> New Game
        </button>
      </div>

      {isLoading && <p className="text-gray-500 text-sm">Loading…</p>}

      <div className="space-y-2">
        {data?.games.map(game => (
          <div key={game.id} className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl px-4 py-3 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-white font-medium text-sm">{game.name_en}</p>
                <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">{GAME_TYPE_LABELS[game.type]}</span>
                <span className="text-xs text-gray-500">{FREQ_LABELS[game.play_frequency]}</span>
                {game.play_cost_drops > 0 && <span className="text-xs text-blue-400">💧 {game.play_cost_drops} to play</span>}
              </div>
              <div className="flex items-center gap-3 mt-0.5">
                <p className="text-gray-500 text-xs">{game.total_plays ?? 0} plays total</p>
                {(game.starts_at || game.ends_at) && (
                  <p className="text-gray-500 text-xs">
                    {game.starts_at ? new Date(game.starts_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '∞'}
                    {' → '}
                    {game.ends_at ? new Date(game.ends_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '∞'}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => toggleActive.mutate({ id: game.id, active: game.active })}
                className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full transition-colors ${game.active ? 'bg-green-500/15 text-green-400' : 'bg-white/5 text-gray-500'}`}>
                {game.active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                {game.active ? 'Active' : 'Off'}
              </button>
              <button onClick={() => setSelectedId(game.id)}
                className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded bg-white/5 hover:bg-white/10">
                Prizes
              </button>
              <button onClick={() => { setEditingGame(game); setShowForm(true) }} className="text-gray-500 hover:text-white p-1"><Pencil size={14} /></button>
              <button onClick={() => { if (confirm('Delete this game?')) deleteGame.mutate(game.id) }} className="text-gray-500 hover:text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {(showForm || editingGame) && (
        <GameForm game={editingGame ?? undefined} onDone={() => { setShowForm(false); setEditingGame(null) }} />
      )}
      {selectedId && <GamePanel gameId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  )
}
